import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toolsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { KeyRound, ExternalLink, Globe, Copy, Check, Loader2, Plus, X, MoreHorizontal, Trash2, ShieldCheck } from 'lucide-react';

export default function ToolsTab() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenTarget, setRegenTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rollenTarget, setRollenTarget] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [copied, setCopied] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const loadTools = useCallback(async () => {
    try {
      const res = await toolsAPI.list();
      if (res.ok) setTools(await res.clone().json());
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTools(); }, [loadTools]);

  const handleRegenerate = async () => {
    if (!regenTarget) return;
    setActionLoading(regenTarget.tool_id);
    try {
      const res = await toolsAPI.regenerateKey(regenTarget.tool_id);
      if (res.ok) {
        const data = await res.clone().json();
        setNewApiKey(data.api_key);
        toast.success('API-sleutel vernieuwd');
      } else {
        const d = await res.clone().json();
        toast.error(d.detail || 'Fout');
        setRegenTarget(null);
      }
    } catch {
      toast.error('Fout bij vernieuwen');
      setRegenTarget(null);
    }
    setActionLoading('');
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newApiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setActionLoading(target.tool_id);

    let res;
    try {
      res = await toolsAPI.delete(target.tool_id);
    } catch {
      toast.error('Netwerkfout bij verwijderen');
      setActionLoading('');
      return;
    }

    if (res.ok) {
      toast.success(`Tool ${target.display_name} verwijderd`);
      setDeleteTarget(null);
      loadTools();
    } else {
      let detail = '';
      try {
        const d = await res.clone().json();
        detail = d?.detail || '';
      } catch {
        /* body unavailable */
      }
      toast.error(detail || `Fout bij verwijderen (HTTP ${res.status})`);
    }

    setActionLoading('');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#1a2332]">Geregistreerde tools</h3>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press transition-colors duration-150"
          data-testid="tools-add-button"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Tool toevoegen
        </Button>
      </div>

      <Card className="border border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1,2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : tools.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Geen tools geregistreerd</p>
            </div>
          ) : (
            <Table data-testid="tools-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Tool</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden sm:table-cell">Pagina's</TableHead>
                  <TableHead className="hidden md:table-cell">URL</TableHead>
                  <TableHead className="text-right">API-sleutel</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tools.map(t => (
                  <TableRow key={t.tool_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#1a2332] flex items-center justify-center flex-shrink-0">
                          <Globe className="w-4 h-4 text-[#d4a853]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{t.display_name}</p>
                          <p className="text-xs text-slate-500">{t.tool_id}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={t.status === 'actief'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50'
                        : 'bg-red-50 text-red-700 border-red-100 hover:bg-red-50'
                      }>
                        {t.status === 'actief' ? 'Actief' : 'Inactief'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <span className="text-sm text-slate-600">
                              {t.beschikbare_paginas?.length || 0} pagina's
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="max-w-xs">
                              {t.beschikbare_paginas?.map(p => (
                                <span key={p} className="inline-block text-xs bg-slate-100 rounded px-1.5 py-0.5 m-0.5">{p}</span>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <a
                        href={t.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#d4a853] hover:text-[#c79b45] flex items-center gap-1 transition-colors duration-150"
                      >
                        {t.url} <ExternalLink className="w-3 h-3" />
                      </a>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={actionLoading === t.tool_id}
                            data-testid={`tools-row-actions-menu-${t.tool_id}`}
                          >
                            {actionLoading === t.tool_id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <MoreHorizontal className="w-4 h-4" />
                            }
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => { setRegenTarget(t); setNewApiKey(''); }}
                            data-testid={`tools-regenerate-key-item-${t.tool_id}`}
                          >
                            <KeyRound className="w-4 h-4 mr-2" /> API-sleutel regenereren
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setRollenTarget(t)}
                            data-testid={`tools-rollen-item-${t.tool_id}`}
                          >
                            <ShieldCheck className="w-4 h-4 mr-2" /> Rollen beheren
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteTarget(t)}
                            className="text-red-600 focus:text-red-600"
                            data-testid={`tools-delete-item-${t.tool_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Verwijderen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Regenerate key dialog */}
      <AlertDialog open={!!regenTarget} onOpenChange={() => { setRegenTarget(null); setNewApiKey(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {newApiKey ? 'Nieuwe API-sleutel' : `API-sleutel regenereren voor ${regenTarget?.display_name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {newApiKey ? (
                <div className="space-y-3 mt-2">
                  <p className="text-sm text-amber-600">Kopieer deze sleutel nu. Je kunt hem niet opnieuw bekijken.</p>
                  <div className="flex gap-2">
                    <code className="flex-1 bg-slate-100 p-3 rounded text-xs break-all">{newApiKey}</code>
                    <Button variant="outline" size="sm" onClick={copyKey} className="flex-shrink-0">
                      {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ) : (
                'De huidige API-sleutel wordt ongeldig. Alle integraties moeten worden bijgewerkt met de nieuwe sleutel.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {newApiKey ? (
              <AlertDialogCancel>Sluiten</AlertDialogCancel>
            ) : (
              <>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <AlertDialogAction onClick={handleRegenerate} className="bg-[#d4a853] text-[#1a2332] hover:bg-[#c79b45]">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Regenereren'}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete tool confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="tools-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Tool verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je tool '{deleteTarget?.display_name}' wilt verwijderen? Dit kan niet ongedaan worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tools-delete-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="tools-delete-confirm"
            >
              {actionLoading === deleteTarget?.tool_id
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : 'Verwijderen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add tool dialog */}
      {showAddDialog && (
        <AddToolDialog
          onClose={() => setShowAddDialog(false)}
          onCreated={(apiKey) => {
            loadTools();
            setNewApiKey(apiKey);
          }}
        />
      )}

      {/* Manage roles dialog */}
      {rollenTarget && (
        <ManageRolesDialog
          tool={rollenTarget}
          onClose={() => setRollenTarget(null)}
          onSaved={(updated) => {
            // Update local tool record so UI stays in sync
            setTools(prev => prev.map(t =>
              t.tool_id === updated.tool_id
                ? { ...t, beschikbare_rollen: updated.beschikbare_rollen }
                : t
            ));
          }}
        />
      )}
    </div>
  );
}

function AddToolDialog({ onClose, onCreated }) {
  const [toolId, setToolId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [url, setUrl] = useState('');
  const [pageInput, setPageInput] = useState('');
  const [pages, setPages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [createdApiKey, setCreatedApiKey] = useState('');
  const [copied, setCopied] = useState(false);

  const addPage = () => {
    const trimmed = pageInput.trim().toLowerCase().replace(/\s+/g, '-');
    if (trimmed && !pages.includes(trimmed)) {
      setPages([...pages, trimmed]);
      setPageInput('');
    }
  };

  const removePage = (page) => {
    setPages(pages.filter(p => p !== page));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addPage();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!toolId || !displayName || !url) {
      setError('Vul alle verplichte velden in');
      return;
    }
    setSaving(true);
    try {
      const res = await toolsAPI.create({
        tool_id: toolId.toLowerCase().replace(/\s+/g, '-'),
        display_name: displayName,
        url,
        beschikbare_paginas: pages,
        allowed_redirect_urls: [url],
      });
      if (res.ok) {
        const data = await res.clone().json();
        toast.success(`Tool ${displayName} aangemaakt`);
        setCreatedApiKey(data.api_key);
        onCreated(data.api_key);
      } else {
        const d = await res.clone().json();
        setError(d.detail || 'Fout bij aanmaken');
      }
    } catch {
      setError('Fout bij aanmaken');
    }
    setSaving(false);
  };

  const copyKey = () => {
    navigator.clipboard.writeText(createdApiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={createdApiKey ? undefined : onClose}>
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        {createdApiKey ? (
          <div>
            <h3 className="text-lg font-semibold text-[#1a2332] mb-1">Tool aangemaakt</h3>
            <p className="text-sm text-amber-600 mb-4">Kopieer de API-sleutel nu. Je kunt hem niet opnieuw bekijken.</p>
            <div className="flex gap-2 mb-6">
              <code className="flex-1 bg-slate-100 p-3 rounded text-xs break-all" data-testid="tools-created-api-key">{createdApiKey}</code>
              <Button variant="outline" size="sm" onClick={copyKey} className="flex-shrink-0">
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <Button onClick={onClose} className="w-full bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl">
              Sluiten
            </Button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-[#1a2332] mb-1">Tool toevoegen</h3>
            <p className="text-sm text-slate-500 mb-4">Registreer een nieuwe tool op het Identity Platform.</p>

            {error && (
              <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-3 py-2 mb-4" data-testid="tools-add-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="tool-id" className="text-xs">Tool ID (slug) *</Label>
                  <Input
                    id="tool-id"
                    placeholder="bijv. smart-roster"
                    value={toolId}
                    onChange={e => setToolId(e.target.value)}
                    className="h-9"
                    data-testid="tools-add-tool-id"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="display-name" className="text-xs">Weergavenaam *</Label>
                  <Input
                    id="display-name"
                    placeholder="bijv. SmartRoster"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    className="h-9"
                    data-testid="tools-add-display-name"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="tool-url" className="text-xs">URL *</Label>
                <Input
                  id="tool-url"
                  type="url"
                  placeholder="https://smartroster.truckwash1.cloud"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  className="h-9"
                  data-testid="tools-add-url"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Beschikbare pagina's</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Typ pagina en druk Enter"
                    value={pageInput}
                    onChange={e => setPageInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-9"
                    data-testid="tools-add-page-input"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addPage} className="h-9 px-3">
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {pages.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="tools-add-pages-list">
                    {pages.map(p => (
                      <span key={p} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 pl-2 pr-1 py-1 rounded">
                        {p}
                        <button type="button" onClick={() => removePage(p)} className="hover:bg-slate-200 rounded p-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-xl">
                  Annuleren
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press"
                  data-testid="tools-add-submit"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                  Aanmaken
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function ManageRolesDialog({ tool, onClose, onSaved }) {
  // Roles working copy. Each role: {naam, label, default_paginas, beschrijving}.
  // We attach private UI fields (_uid, _isExisting) for stable React keys and
  // editability — these are stripped before the PUT.
  const [roles, setRoles] = useState([]);
  const [original, setOriginal] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [expanded, setExpanded] = useState(null); // role _uid currently expanded
  const uidCounterRef = useRef(0);

  const beschikbarePaginas = tool.beschikbare_paginas || [];

  const nextUid = () => `new-${++uidCounterRef.current}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await toolsAPI.getRollen(tool.tool_id);
        if (!cancelled && res.ok) {
          const data = await res.clone().json();
          const raw = Array.isArray(data.beschikbare_rollen) ? data.beschikbare_rollen : [];
          // Stable _uid for existing roles = their (immutable) naam
          const list = raw.map(r => ({ ...r, _uid: `existing-${r.naam}`, _isExisting: true }));
          setRoles(list);
          setOriginal(list);
        }
      } catch { /* ignore */ }
      if (!cancelled) setLoadingInitial(false);
    })();
    return () => { cancelled = true; };
  }, [tool.tool_id]);

  // Dirty check: compare canonical (UI-fields stripped) shapes
  const stripUi = (r) => {
    const { _uid, _isExisting, ...rest } = r;
    return rest;
  };
  const dirty = JSON.stringify(roles.map(stripUi)) !== JSON.stringify(original.map(stripUi));

  const addRole = () => {
    const fresh = {
      _uid: nextUid(),
      _isExisting: false,
      naam: '',
      label: '',
      default_paginas: {},
      beschrijving: '',
    };
    setRoles([...roles, fresh]);
    setExpanded(fresh._uid);
    setError('');
  };

  const updateRole = (idx, patch) => {
    setRoles(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    setError('');
  };

  const togglePaginaDefault = (idx, pagina) => {
    setRoles(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const cur = r.default_paginas || {};
      return { ...r, default_paginas: { ...cur, [pagina]: !cur[pagina] } };
    }));
  };

  const removeRole = (idx) => {
    setRoles(prev => prev.filter((_, i) => i !== idx));
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    // Client-side guards
    const naams = roles.map(r => (r.naam || '').trim().toLowerCase());
    const emptyIdx = naams.findIndex(n => !n);
    if (emptyIdx >= 0) {
      setError(`Rolnaam mag niet leeg zijn (regel ${emptyIdx + 1})`);
      setSaving(false);
      return;
    }
    const dup = naams.find((n, i) => naams.indexOf(n) !== i);
    if (dup) {
      setError(`Rol '${dup}' staat dubbel in de lijst`);
      setSaving(false);
      return;
    }
    // Strip UI-only fields before PUT
    const payload = roles.map(stripUi);
    let res;
    try {
      res = await toolsAPI.updateRollen(tool.tool_id, payload);
    } catch {
      setError('Netwerkfout bij opslaan');
      setSaving(false);
      return;
    }
    if (res.ok) {
      const data = await res.clone().json();
      toast.success('Rollen bijgewerkt');
      onSaved({ tool_id: tool.tool_id, beschikbare_rollen: data.beschikbare_rollen });
      onClose();
    } else {
      let detail = '';
      try { detail = (await res.clone().json())?.detail || ''; } catch { /* ignore */ }
      setError(typeof detail === 'string' ? detail : `Fout bij opslaan (HTTP ${res.status})`);
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={saving ? undefined : onClose}
      data-testid="tools-rollen-dialog"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 pb-4 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-[#1a2332]">Rollen beheren — {tool.display_name}</h3>
          <p className="text-sm text-slate-500 mt-1">
            Definieer tool-specifieke rollen met optionele standaard pagina-rechten. De rolnaam (slug) is vast na opslaan; label, beschrijving en standaard-rechten kun je later wijzigen.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="tools-rollen-error">
              {error}
            </div>
          )}

          {loadingInitial ? (
            <Skeleton className="h-20 w-full" />
          ) : roles.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400 italic" data-testid="tools-rollen-empty">
              Nog geen rollen gedefinieerd.
            </div>
          ) : (
            <div className="space-y-2" data-testid="tools-rollen-list">
              {roles.map((r, idx) => {
                const isExisting = r._isExisting;
                const isExpanded = expanded === r._uid;
                const defaultsCount = Object.values(r.default_paginas || {}).filter(Boolean).length;
                const cardTestId = isExisting ? r.naam : `nieuw-${idx}`;
                return (
                  <Card key={r._uid} className="border border-slate-200" data-testid={`tools-rollen-card-${cardTestId}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Naam (slug)</Label>
                            <Input
                              value={r.naam}
                              onChange={e => updateRole(idx, { naam: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                              disabled={isExisting}
                              placeholder="bijv. manager"
                              className="h-8 text-sm font-mono"
                              data-testid={`tools-rollen-naam-${idx}`}
                            />
                            {isExisting && (
                              <p className="text-[10px] text-slate-400 mt-1">Naam is vast (immutable).</p>
                            )}
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Label</Label>
                            <Input
                              value={r.label}
                              onChange={e => updateRole(idx, { label: e.target.value })}
                              placeholder="bijv. Manager"
                              className="h-8 text-sm"
                              data-testid={`tools-rollen-label-${idx}`}
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRole(idx)}
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`tools-rollen-remove-${cardTestId}`}
                          aria-label={`Verwijder rol ${r.naam || idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Beschrijving (optioneel)</Label>
                        <Textarea
                          value={r.beschrijving || ''}
                          onChange={e => updateRole(idx, { beschrijving: e.target.value })}
                          placeholder="Korte uitleg van wat deze rol doet"
                          rows={2}
                          className="text-sm resize-none"
                          data-testid={`tools-rollen-beschrijving-${idx}`}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => setExpanded(isExpanded ? null : r._uid)}
                        className="w-full flex items-center justify-between text-xs font-medium text-[#1a2332] hover:text-[#d4a853] transition-colors py-1.5 px-2 rounded hover:bg-slate-50"
                        data-testid={`tools-rollen-expand-${cardTestId}`}
                      >
                        <span>
                          Standaard pagina-rechten
                          <span className="ml-2 text-slate-400 font-normal">
                            ({defaultsCount} van {beschikbarePaginas.length})
                          </span>
                        </span>
                        <span className="text-slate-400">{isExpanded ? '▾' : '▸'}</span>
                      </button>

                      {isExpanded && (
                        <div className="pt-1 border-t border-slate-100">
                          {beschikbarePaginas.length === 0 ? (
                            <p className="text-xs text-slate-400 italic p-2">Deze tool heeft geen pagina's gedefinieerd.</p>
                          ) : (
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 p-2" data-testid={`tools-rollen-defaults-${cardTestId}`}>
                              {beschikbarePaginas.map(p => (
                                <label
                                  key={p}
                                  className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:text-[#1a2332]"
                                >
                                  <Checkbox
                                    checked={!!r.default_paginas?.[p]}
                                    onCheckedChange={() => togglePaginaDefault(idx, p)}
                                    data-testid={`tools-rollen-default-${cardTestId}-${p}`}
                                  />
                                  <span className="capitalize">{p}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={addRole}
            className="w-full rounded-xl border-dashed"
            data-testid="tools-rollen-add"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Rol toevoegen
          </Button>
        </div>

        <div className="p-6 pt-4 border-t border-slate-100 flex gap-3">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving} className="flex-1 rounded-xl" data-testid="tools-rollen-cancel">
            Annuleren
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="flex-1 bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press"
            data-testid="tools-rollen-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Opslaan
          </Button>
        </div>
      </div>
    </div>
  );
}

